import { FunctionComponent, useEffect, useState } from 'react';
import { TextField, Typography } from '@mui/material';
import {
    PRODUCT_TEXT_MAX_LENGTH,
    useGetProductTextQuery,
    usePutProductTextMutation,
} from '@starjack/entities/assistantConfig';
import { useNotification } from '@starjack/features/notifications';
import { SectionCard } from './SectionCard';

type ProductAiInfoSectionProps = {
    productId: string;
};

export const ProductAiInfoSection: FunctionComponent<
    ProductAiInfoSectionProps
> = ({ productId }) => {
    const { currentData, isError } = useGetProductTextQuery(productId, {
        skip: !productId,
    });
    const [putProductText, { isLoading }] = usePutProductTextMutation();
    const { addNotification } = useNotification();
    const [text, setText] = useState('');

    useEffect(() => {
        if (currentData) setText(currentData.text);
    }, [currentData]);

    const onSave = () => {
        putProductText({ productId, text })
            .unwrap()
            .then(() => {
                addNotification({
                    message: 'Konfiguration erfolgreich gespeichert',
                    type: 'success',
                });
            })
            .catch(() => {
                addNotification({
                    message:
                        'Fehler beim Speichern. Bitte später noch einmal versuchen',
                    type: 'error',
                });
            });
    };

    const isReady = !!currentData && !isError;

    // No save until the stored text is loaded: saving the empty initial field
    // would delete the operator's note.
    return (
        <SectionCard
            title='KI-Informationen'
            onSave={onSave}
            isSaving={isLoading}
            hideActions={!isReady}
        >
            <Typography variant='subtitle1' gutterBottom>
                Fakten zu diesem Ticket, die der KI-Assistent kennen soll (z. B.
                welche Lifte enthalten sind oder für wen es passt). Der Text
                wird Kunden nicht wörtlich angezeigt. Keine vertraulichen
                Angaben eintragen.
            </Typography>
            {isError && !currentData ? (
                <Typography color='error'>
                    Die KI-Informationen konnten nicht geladen werden. Bitte
                    später noch einmal versuchen.
                </Typography>
            ) : (
                <TextField
                    multiline
                    minRows={4}
                    fullWidth
                    disabled={!isReady}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    inputProps={{ maxLength: PRODUCT_TEXT_MAX_LENGTH }}
                    helperText={`${text.length}/${PRODUCT_TEXT_MAX_LENGTH}`}
                />
            )}
        </SectionCard>
    );
};
